import { ShopTypes } from '~/lib/data-contract';
import { WarehouseStockDetails } from '~/lib/data-contract/auto-generated/shop-api-model';
import { useStockDetails } from '~/services/shop/hooks';
import { useTranslations } from '~/services/umbraco/hooks';
import { Flex } from '~/shared/components/Basics';
import { Button } from '~/shared/components/Button';
import { ErrorMessage } from '~/shared/components/Form';
import { LoadingSpinner } from '~/shared/components/LoadingSpinner';
import { ProductOnlineStatus } from '~/shared/components/ProductOnlineStatus';
import { Text } from '~/shared/components/Text';
import { useIsMounted } from '~/shared/hooks';
import {
    FreightTypes,
    useFreightType,
    usePostalCode,
    useWarehouseId,
} from '~/shared/utils/storage';
import { setActiveSetWarehouseId, useModalFreight } from '~/state/useModalFreight';
import {
    DeliveryStatus,
    DeliveryText,
    Loader,
    RadioButton,
    SectionSelector,
    StyledList,
    StyledNotification,
    Wrapper,
} from './styled';
import { useSession } from 'next-auth/react';
import { useMemo } from 'react';

interface PickUpItemProps extends WarehouseStockDetails {
    activeWarehouseId: number | undefined;
    canCreateOrder: boolean;
}

type ModalProps = {
    modalType: 'quickAdd' | 'freight';
};
export default function ProductStatusModal({ modalType }: ModalProps) {
    const { itemNo, activeWarehouseId, errorMessages } = useModalFreight();
    const { translate } = useTranslations();
    const { status } = useSession();

    const [postalCode] = usePostalCode();
    const { data: stockDetails, isLoading, isError } = useStockDetails({ itemNo, postalCode });

    const isMounted = useIsMounted();

    const canCreateOrder = useMemo(() => status === 'unauthenticated', [status]);

    const pickup = stockDetails?.pickup;
    if (isLoading && !isError) {
        return <Loading isActive={isMounted()} />;
    }

    return (
        <>
            <Text variant="display2">
                {canCreateOrder
                    ? translate('freightCalculator.chooseLocation')
                    : translate('freightCalculator.stockStatus')}
            </Text>

            {canCreateOrder ? <Text>{translate('freightCalculator.freightText')}</Text> : null}

            <StyledList>
                {pickup?.warehouses?.map((data) => (
                    <li key={data.id}>
                        <PickUpItem
                            {...data}
                            activeWarehouseId={activeWarehouseId}
                            canCreateOrder={canCreateOrder}
                        />
                    </li>
                ))}
            </StyledList>
            <div>
                {canCreateOrder ? (
                    <>
                        {errorMessages && <ErrorMessage>{errorMessages}</ErrorMessage>}
                        <Button
                            form={modalType}
                            type="submit"
                            disabled={activeWarehouseId === undefined}
                            fullWidth
                        >
                            {translate('generic.addToBasket')}
                        </Button>
                    </>
                ) : null}
            </div>
        </>
    );
}

function PickUpItem({
    statusText,
    activeWarehouseId,
    name,
    title,
    status,
    id,
    canCreateOrder,
}: PickUpItemProps) {
    const { translate } = useTranslations();

    const [, setWarehouseId] = useWarehouseId();
    const [, setFreightType] = useFreightType();

    const onChangeHandler = (id?: number) => {
        const value = Number(id) || undefined;

        setWarehouseId(value);
        setActiveSetWarehouseId(value);
        setFreightType(FreightTypes.Pickup);
    };

    const quantityObject = statusText?.values?.find((item) => item.key === 'QUANTITY');

    return (
        <Wrapper
            isDisabled={status === ShopTypes.StockStatus.OutOfStock}
            onClick={() => {
                if (canCreateOrder) onChangeHandler(id);
            }}
            onKeyDown={(key) =>
                canCreateOrder && (key.code === '13' || key.code === 'Enter')
                    ? onChangeHandler(id)
                    : null
            }
            tabIndex={0}
            canCreateOrder={canCreateOrder}
        >
            <Flex align="center" flow="row">
                {canCreateOrder ? (
                    <RadioButton
                        name="pickup"
                        onChange={() => undefined}
                        disabled={status === ShopTypes.StockStatus.OutOfStock}
                        checked={activeWarehouseId === id}
                    />
                ) : null}

                <Flex flow="column">
                    <Text>{name}</Text>
                    {canCreateOrder ? (
                        <DeliveryText>{translate(title?.name, title?.values)}</DeliveryText>
                    ) : null}
                </Flex>
            </Flex>

            <Flex flow="column">
                {statusText ? (
                    <SectionSelector>
                        <DeliveryStatus>
                            <ProductOnlineStatus
                                text={translate(statusText?.name, statusText?.values)}
                                status={status}
                            />
                        </DeliveryStatus>
                    </SectionSelector>
                ) : null}
            </Flex>
            {quantityObject?.value === 1 && (
                <StyledNotification
                    content={translate('freightCalculator.exhibitionItem')}
                    variant="low"
                    canCreateOrder={canCreateOrder}
                />
            )}
        </Wrapper>
    );
}

function Loading(props: { isActive: boolean }) {
    return (
        <Loader {...props}>
            <LoadingSpinner />
        </Loader>
    );
}
